import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// Renders a tooltip into document.body and positions it relative to anchorRect
export default function CellTooltip({ visible, anchorRect, children, offset = 8, className = '' }) {
  const ref = useRef(null);
  const [style, setStyle] = useState({ left: 0, top: 0, opacity: 0 });
  const [placement, setPlacement] = useState('top');

  useEffect(() => {
    if (!visible || !anchorRect) {
      setStyle(prev => ({ ...prev, opacity: 0 }));
      return;
    }

    const update = () => {
      const el = ref.current;
      if (!el) return;
      const ttRect = el.getBoundingClientRect();
      const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
      const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);

      // Prefer above if there is enough space, otherwise below
      const spaceAbove = anchorRect.top;
      const spaceBelow = vh - (anchorRect.top + anchorRect.height);
      const preferTop = spaceAbove >= ttRect.height + offset;
      const place = preferTop ? 'top' : 'bottom';

      let left = anchorRect.left + anchorRect.width / 2 - ttRect.width / 2;
      // clamp horizontally
      left = Math.max(8, Math.min(left, vw - ttRect.width - 8));

      let top;
      if (place === 'top') {
        top = anchorRect.top - ttRect.height - offset;
      } else {
        top = anchorRect.top + anchorRect.height + offset;
      }

      setPlacement(place);
      setStyle({ left: Math.round(left), top: Math.round(top), opacity: 1 });
    };

    // Measure after paint
    requestAnimationFrame(update);

    // Also update on scroll/resize
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [visible, anchorRect, offset]);

  if (typeof document === 'undefined') return null;
  if (!visible) return null;

  const tooltip = (
    <div
      ref={ref}
      className={`portal-tooltip ${className} placement-${placement}`}
      role="tooltip"
      style={{ position: 'fixed', left: style.left, top: style.top, opacity: style.opacity }}
    >
      {children}
    </div>
  );

  return createPortal(tooltip, document.body);
}
